import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { mediaUrl } from '../lib/sightingsApi'

const MIN_SCALE = 1
const MAX_SCALE = 5
const ZOOM_STEP = 0.35

interface Props {
  imageKeys: string[]
  startIndex: number
  alt: string
  onClose: () => void
}

export function PhotoGallery({ imageKeys, startIndex, alt, onClose }: Props) {
  const { t } = useTranslation()
  const [index, setIndex] = useState(
    Math.min(Math.max(0, startIndex), Math.max(0, imageKeys.length - 1)),
  )
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
  } | null>(null)
  const pinchRef = useRef<{
    startDistance: number
    startScale: number
  } | null>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  const count = imageKeys.length
  const currentKey = imageKeys[index]
  const canPrev = index > 0
  const canNext = index < count - 1

  function resetView() {
    setScale(1)
    setOffset({ x: 0, y: 0 })
    dragRef.current = null
    pinchRef.current = null
  }

  function goTo(next: number) {
    if (next < 0 || next >= count) return
    setIndex(next)
    resetView()
  }

  function clampScale(value: number) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value))
  }

  function zoomBy(delta: number, origin?: { x: number; y: number }) {
    setScale((prev) => {
      const next = clampScale(prev + delta)
      if (next === MIN_SCALE) {
        setOffset({ x: 0, y: 0 })
        return next
      }
      if (origin && stageRef.current) {
        const rect = stageRef.current.getBoundingClientRect()
        const cx = origin.x - rect.left - rect.width / 2
        const cy = origin.y - rect.top - rect.height / 2
        const ratio = next / prev
        setOffset((off) => ({
          x: cx - (cx - off.x) * ratio,
          y: cy - (cy - off.y) * ratio,
        }))
      }
      return next
    })
  }

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        setIndex((current) => {
          if (current <= 0) return current
          setScale(1)
          setOffset({ x: 0, y: 0 })
          return current - 1
        })
        return
      }
      if (
        event.key === 'ArrowRight' ||
        event.key === 'PageDown' ||
        event.key === ' '
      ) {
        event.preventDefault()
        setIndex((current) => {
          if (current >= count - 1) return current
          setScale(1)
          setOffset({ x: 0, y: 0 })
          return current + 1
        })
        return
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        setScale((prev) => clampScale(prev + ZOOM_STEP))
        return
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        setScale((prev) => {
          const next = clampScale(prev - ZOOM_STEP)
          if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
          return next
        })
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, count])

  function onWheel(event: ReactWheelEvent<HTMLDivElement>) {
    event.preventDefault()
    const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP
    zoomBy(delta, { x: event.clientX, y: event.clientY })
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch') return
    if (scale <= MIN_SCALE) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    }
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    })
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null
    }
  }

  function onDoubleClick(event: ReactPointerEvent<HTMLDivElement>) {
    if (scale > MIN_SCALE) {
      resetView()
      return
    }
    zoomBy(1.5, { x: event.clientX, y: event.clientY })
  }

  function touchDistance(touches: { length: number; [index: number]: { clientX: number; clientY: number } }) {
    if (touches.length < 2) return 0
    const a = touches[0]
    const b = touches[1]
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
  }

  function onTouchStart(event: React.TouchEvent<HTMLDivElement>) {
    if (event.touches.length === 2) {
      pinchRef.current = {
        startDistance: touchDistance(event.touches),
        startScale: scale,
      }
      dragRef.current = null
      return
    }
    if (event.touches.length === 1 && scale > MIN_SCALE) {
      const touch = event.touches[0]
      dragRef.current = {
        pointerId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        originX: offset.x,
        originY: offset.y,
      }
    }
  }

  function onTouchMove(event: React.TouchEvent<HTMLDivElement>) {
    if (event.touches.length === 2 && pinchRef.current) {
      event.preventDefault()
      const distance = touchDistance(event.touches)
      if (pinchRef.current.startDistance <= 0) return
      const next = clampScale(
        pinchRef.current.startScale *
          (distance / pinchRef.current.startDistance),
      )
      setScale(next)
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
      return
    }
    const drag = dragRef.current
    if (!drag || event.touches.length !== 1) return
    event.preventDefault()
    const touch = event.touches[0]
    setOffset({
      x: drag.originX + (touch.clientX - drag.startX),
      y: drag.originY + (touch.clientY - drag.startY),
    })
  }

  function onTouchEnd() {
    if (pinchRef.current) pinchRef.current = null
    dragRef.current = null
  }

  if (!currentKey) return null

  return createPortal(
    <div
      className="photo-gallery"
      role="dialog"
      aria-modal="true"
      aria-label={t('gallery.title')}
    >
      <button
        type="button"
        className="photo-gallery-backdrop"
        aria-label={t('gallery.close')}
        onClick={onClose}
      />

      <div className="photo-gallery-chrome">
        <div className="photo-gallery-meta">
          <span>
            {t('gallery.counter', { current: index + 1, total: count })}
          </span>
          <span className="photo-gallery-hint">{t('gallery.zoomHint')}</span>
        </div>
        <div className="photo-gallery-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => zoomBy(-ZOOM_STEP)}
            disabled={scale <= MIN_SCALE}
          >
            {t('gallery.zoomOut')}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={scale >= MAX_SCALE}
          >
            {t('gallery.zoomIn')}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={resetView}
            disabled={scale <= MIN_SCALE}
          >
            {t('gallery.resetZoom')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('gallery.close')}
          </button>
        </div>
      </div>

      <div className="photo-gallery-stage-wrap">
        {count > 1 && (
          <button
            type="button"
            className="photo-gallery-nav prev"
            onClick={() => goTo(index - 1)}
            disabled={!canPrev}
            aria-label={t('gallery.prev')}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M15.5 4.5 8 12l7.5 7.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div
          ref={stageRef}
          className={`photo-gallery-stage${scale > MIN_SCALE ? ' zoomed' : ''}`}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <img
            src={mediaUrl(currentKey)}
            alt={alt}
            draggable={false}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            }}
          />
        </div>

        {count > 1 && (
          <button
            type="button"
            className="photo-gallery-nav next"
            onClick={() => goTo(index + 1)}
            disabled={!canNext}
            aria-label={t('gallery.next')}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M8.5 4.5 16 12l-7.5 7.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="photo-gallery-thumbs" role="tablist" aria-label={t('gallery.thumbs')}>
          {imageKeys.map((key, thumbIndex) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={thumbIndex === index}
              className={`photo-gallery-thumb${thumbIndex === index ? ' active' : ''}`}
              onClick={() => goTo(thumbIndex)}
            >
              <img src={mediaUrl(key)} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  )
}
